import React, { useEffect, useState, useCallback, useRef } from 'react';
import BackToTop from '../components/BackToTop';
import VirtualPhotoGallery from '../components/VirtualPhotoGallery';
import { setMetaTags } from '../utils/metaTags';

interface PhotographsProps {
    theme: 'light' | 'dark';
}

const Photographs: React.FC<PhotographsProps> = ({ theme }) => {
    const [currentIndex, setCurrentIndex] = useState<number>(-1);
    const contentRef = useRef<HTMLDivElement>(null);
    const imageCount = 61;
    const asia35mm = Array.from({ length: imageCount }, (_, i) => `/images/asia-35mm-optimized/${i + 1}.jpg`);

    // Set dynamic meta tags for this page
    useEffect(() => {
        setMetaTags({
            title: "Photography",
            description: "Film photography from SE Asia - 35mm street photography, travel moments, and cultural scenes captured on analog film.",
            url: window.location.href
        });
    }, []);

    const scrollToImage = useCallback((index: number) => {
        const newIndex = Math.max(0, Math.min(index, imageCount - 1));
        setCurrentIndex(newIndex);
        
        const target = document.querySelector(`#image-${newIndex}`);
        if (target) {
            const offset = 140;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = target.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    }, [imageCount]);

    const scrollToTop = useCallback(() => {
        setCurrentIndex(-1);
        const element = document.getElementById('se-asia-35mm');
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, []);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if ((e.altKey || e.metaKey) && e.key.toLowerCase() === 't') {
            e.preventDefault();
            scrollToTop();
            return;
        }

        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            const newIndex = currentIndex === -1 ? 0 : currentIndex + 1;
            if (newIndex < imageCount) {
                scrollToImage(newIndex);
            }
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            if (currentIndex <= 0) {
                scrollToTop();
            } else {
                scrollToImage(currentIndex - 1);
            }
        }
    }, [currentIndex, imageCount, scrollToImage, scrollToTop]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
        <div ref={contentRef} className={`max-w-[652px] ${theme === 'light' ? 'bg-white' : ''}`}>
            <div className="space-y-12">
                <section id="se-asia-35mm">
                    <h2 className="mb-6 text-gray-500">
                        🚧 More soon…{' '}
                        <span className="hidden md:inline">use keyboard arrows or scroll to view</span>
                        <span className="md:hidden">scroll to view</span>
                    </h2>
                    <VirtualPhotoGallery 
                        images={asia35mm}
                        theme={theme}
                        onImageFocus={setCurrentIndex}
                        currentIndex={currentIndex}
                    />
                </section>
            </div>
            
            <BackToTop theme={theme} />
        </div>
    );
};

export default Photographs; 